import React, { useState, useRef, useEffect } from 'react';
import { Grid, Box, Typography, Button, FormControl, InputLabel, OutlinedInput, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { Search, Refresh, VisibilityOutlined } from '@mui/icons-material';
import { useGetProductDetailsQuery } from '../../Shared/Store/Api/productsdeatilsApi';
import DynamicSearchBox from '../../Common/DynamicSearchBox';
import { cardItem, colorTheme } from '../../Common/Theme';

function ProductDetails() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProducts, setSelectedProducts] = useState<any[]>(() => {
        const saved = localStorage.getItem('productDetails_selectedProducts');
        return saved ? JSON.parse(saved) : [];
    });
    const [scannedCode, setScannedCode] = useState('');
    const [viewingProduct, setViewingProduct] = useState<any>(null);

    const { data: productDetails, refetch } = useGetProductDetailsQuery(
        { scannedCode },
    );
    console.log(scannedCode, 'scannedCode');
    const searchProduct = useRef<HTMLDivElement>(null);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchQuery(value);
        setScannedCode(value);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchQuery.trim()) {
            setScannedCode(searchQuery.trim());
            setSearchQuery('');
        }
    };

    const handleBarcodeScan = () => {
        if (searchQuery.trim()) {
            setScannedCode(searchQuery.trim());
            setSearchQuery('');
        }
    };

    const handleReset = () => {
        setSelectedProducts([]);
        setSearchQuery('');
        setScannedCode('');
        setViewingProduct(null);
        localStorage.removeItem('productDetails_selectedProducts');
    };

    const handleProductClick = (product: any) => {
        setSearchQuery('');
        addProductToTable(product);
    };

    const addProductToTable = (product: any) => {
        const existingProduct = selectedProducts.find(p => p.barCode === product.prod_code);
        if (existingProduct) {
            const updatedProducts = selectedProducts.map(p =>
                p.barCode === product.prod_code
                    ? { ...p, quantity_at_hand: p.quantity_at_hand + 1 }
                    : p
            );
            setSelectedProducts(updatedProducts);
            const updatedProduct = updatedProducts.find(p => p.barCode === product.prod_code);
            if (updatedProduct) {
                setViewingProduct(updatedProduct);
            }
        } else {
            const newProduct: any = {
                barCode: product.prod_code,
                prod_title: product.prod_title,
                prod_sku: product.prod_sku,
                size: product.sizes,
                color: product.colors,
                quantity_at_hand: product.quantity || 1,
                avg_selling_rate: product.retail_price || 0,
                mrp: product.mrp || 0,
                wh_price: product.wh_price || 0,
                reseller_price: product.reseller_price || 0,
                retail_price: product.retail_price || 0
            };
            setSelectedProducts(prev => [newProduct, ...prev]);
            setViewingProduct(newProduct);
        }
    };

    const handleViewProductDetails = (product: any) => {
        setViewingProduct(product);
    };

    const saveProductsToStorage = (products: any[]) => {
        localStorage.setItem('productDetails_selectedProducts', JSON.stringify(products));
    };

    useEffect(() => {
        saveProductsToStorage(selectedProducts);
    }, [selectedProducts]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (searchProduct.current && !searchProduct.current.contains(event.target)) {
                setSearchQuery('');
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (searchQuery) {
            refetch();
        }
    }, [searchQuery, refetch]);

    useEffect(() => {
        if (productDetails && productDetails.prod_code && scannedCode) {
            const existingProduct = selectedProducts.find(p => p.barCode === productDetails.prod_code);
            if (!existingProduct) {
                addProductToTable(productDetails);
                setScannedCode('');
                setSearchQuery('');
            }
        }
    }, [productDetails, scannedCode, selectedProducts]);

    return (
        <section id="main" className="flex flex-col overflow-auto flex-1 gap-3" style={{ padding: '5px' }}>
            <Grid container spacing={2} marginTop={1}>
                <Grid item xs={12} md={8}>
                    <Box sx={{ ...cardItem, height: '100%' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Grid container justifyContent="space-between" alignItems='center' sx={{ mb: 2 }}>
                                    <Grid item xs={6}>
                                        <DynamicSearchBox>
                                            <FormControl variant="outlined" sx={{ borderColor: '#bbb', width: '100%' }} size='small'>
                                                <InputLabel htmlFor="outlined-adornment-password">Search product/barcode</InputLabel>
                                                <OutlinedInput
                                                    value={searchQuery}
                                                    onChange={handleSearchChange}
                                                    type='text'
                                                    onKeyPress={handleKeyPress}
                                                    endAdornment={
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={handleBarcodeScan}>
                                                                <Search />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    }
                                                    label="Search product/barcode"
                                                />
                                            </FormControl>
                                        </DynamicSearchBox>
                                        {searchQuery && (
                                            productDetails && productDetails.prod_code && (
                                                <div ref={searchProduct} className="search-result bg-white flex relative">
                                                    <ul className="overflow-auto flex-1 absolute top-0 end-0 start-0 h-96 bg-white shadow-2xl z-10">
                                                        <li className={`border-b border-slate-200 p-2 flex items-center gap-2 text-black justify-between hover:bg-primary
                                                                 hover:text-white cursor-pointer`}
                                                            onClick={() => handleProductClick(productDetails)}>
                                                            <span className="flex-1">{productDetails.prod_title}</span>
                                                            <sub><strong className="text-uppercase">{productDetails.prod_code}</strong></sub>
                                                            <strong>SKU: {productDetails.prod_sku}</strong>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )
                                        )}
                                    </Grid>
                                    <Grid item xs={2} alignItems='end'>
                                        <Button
                                            variant="outlined"
                                            onClick={handleReset}
                                            startIcon={<Refresh />}
                                            sx={{
                                                borderColor: '#666',
                                                color: '#666',
                                                '&:hover': { borderColor: '#333', color: '#333' }
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                    minHeight: '400px',
                                    maxHeight: '400px',
                                    height: '400px',
                                }}>
                                    <table className="w-full customTable">
                                        <thead>
                                            <tr>
                                                <th className="text-left">Barcode</th>
                                                <th className="text-left">Product Name</th>
                                                <th className="text-center">Qty</th>
                                                <th className="text-left">WH Price</th>
                                                <th className="text-left">Reseller Price</th>
                                                <th className="text-left">Retail Price</th>
                                                <th className="text-left">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedProducts.map((product: any, index: number) => (
                                                <tr key={index}>
                                                    <td>
                                                        <Grid container spacing={0} alignItems='center'>
                                                            <Grid item xs={12}>
                                                                {product.barCode}
                                                            </Grid>
                                                            <Grid item xs='auto'>
                                                                <Typography variant="body1" sx={{ fontSize: '12px', fontWeight: '600' }}>
                                                                    SKU:&nbsp;{product.prod_sku}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </td>
                                                    <td className="text-nowrap">
                                                        <Grid container spacing={0} direction="column">
                                                            <Grid item>
                                                                {product.prod_title}
                                                            </Grid>
                                                            <Grid item>
                                                                <Grid container spacing={1}>
                                                                    {product.size && (
                                                                        <Grid item xs='auto'>
                                                                            Size:&nbsp;{product.size}
                                                                        </Grid>
                                                                    )}
                                                                    {product.color && (
                                                                        <Grid item xs='auto'>
                                                                            Color:&nbsp;{product.color}
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="text-lg">{product.quantity_at_hand}</span>
                                                    </td>
                                                    <td>
                                                        <Typography variant="body1">
                                                            ₹{product.wh_price?.toFixed(2) || '0.00'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography variant="body1">
                                                            ₹{product.reseller_price?.toFixed(2) || '0.00'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Typography variant="body1">
                                                            ₹{product.retail_price?.toFixed(2) || '0.00'}
                                                        </Typography>
                                                    </td>
                                                    <td>
                                                        <Tooltip placement='left' title="View details">
                                                            <Button
                                                                onClick={() => handleViewProductDetails(product)}
                                                                sx={{ padding: 0, border: 0, minWidth: '24px', color: colorTheme.palette.topLink, '&:hover': { border: 0, color: colorTheme.palette.indigo500 }, fontWeight: '600' }}
                                                                variant="text"
                                                            >
                                                                <VisibilityOutlined />
                                                            </Button>
                                                        </Tooltip>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {selectedProducts.length === 0 && (
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '200px',
                                            color: '#999'
                                        }}>
                                            <Typography variant="body1">No products selected.</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ ...cardItem, height: '100%' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant='h6' sx={{ fontWeight: 600, color: colorTheme.palette.primary.main, mb: 2 }}>
                                    Product Details
                                </Typography>

                                {viewingProduct ? (
                                    <Box>
                                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: colorTheme.palette.primary.main, mb: 2 }}>
                                                {viewingProduct.prod_title}
                                            </Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                        Barcode:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {viewingProduct.barCode}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                        SKU:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {viewingProduct.prod_sku}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                        Quantity:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {viewingProduct.quantity_at_hand} pcs
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                        MRP:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        ₹{viewingProduct.mrp?.toFixed(2) || '0.00'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                        Colors:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {viewingProduct.color || 'N/A'}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                        Sizes:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {viewingProduct.size || 'N/A'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colorTheme.palette.primary.main, mb: 2 }}>
                                                Pricing Details
                                            </Typography>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Box sx={{ p: 2, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #dee2e6' }}>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={6}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                                    Warehouse Price:
                                                                </Typography>
                                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#28a745' }}>
                                                                    ₹{viewingProduct.wh_price?.toFixed(2) || '0.00'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                                    Reseller Price:
                                                                </Typography>
                                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#007bff' }}>
                                                                    ₹{viewingProduct.reseller_price?.toFixed(2) || '0.00'}
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                                                                    Retail Price:
                                                                </Typography>
                                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#dc3545' }}>
                                                                    ₹{viewingProduct.retail_price?.toFixed(2) || '0.00'}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>

                                        <Box sx={{ textAlign: 'center' }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setViewingProduct(null)}
                                                sx={{
                                                    borderColor: '#666',
                                                    color: '#666',
                                                    '&:hover': { borderColor: '#333', color: '#333' }
                                                }}
                                            >
                                                Close Details
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', color: '#999', py: 4 }}>
                                        <Typography variant="body1">
                                            Click the eye icon on any product to view detailed information
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </section>
    );
}

export default ProductDetails;
